"use client"

import React, { useState } from "react"
import { findOrderByDisplayId } from "@lib/data/orders"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { convertToLocale } from "@lib/util/money"
import { HttpTypes } from "@medusajs/types"

const toLabel = (s: string) =>
  s.split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")

const chipClass = (s: string) => {
  const l = s.toLowerCase()
  if (l.includes("cancel")) return "tro-chip tro-chip-cancelled"
  if (["complet", "delivered", "fulfilled"].some((k) => l.includes(k)))
    return "tro-chip tro-chip-completed"
  if (["pending", "awaiting", "authorized", "not_paid"].some((k) => l.includes(k)))
    return "tro-chip tro-chip-pending"
  return "tro-chip tro-chip-default"
}

const OrderResult: React.FC<{ order: HttpTypes.StoreOrder }> = ({ order }) => {
  const createdAt = new Date(order.created_at as unknown as string)
  const formattedDate = createdAt.toLocaleDateString("en-GB", {
    day: "numeric", month: "long", year: "numeric",
  })
  const total = convertToLocale({ amount: order.total, currency_code: order.currency_code })
  const status = toLabel(order.status)

  return (
    <div className="tro-result-box">
      <div className="tro-result-header">
        <span className="tro-result-label">Order #{order.display_id}</span>
        <span className={chipClass(order.status)}>{status}</span>
      </div>
      <div className="tro-result-rows">
        <div className="tro-result-row">
          <span className="tro-result-key">Placed:</span>
          <span className="tro-result-val">{formattedDate}</span>
        </div>
        <div className="tro-result-row">
          <span className="tro-result-key">Total:</span>
          <span className="tro-result-val tro-result-total">{total}</span>
        </div>
        {order.items && order.items.length > 0 && (
          <div className="tro-result-row tro-result-row-items">
            <span className="tro-result-key">Items:</span>
            <span className="tro-result-val">{order.items.map((i) => `${i.product_title} ×${i.quantity}`).join(", ")}</span>
          </div>
        )}
      </div>
      <LocalizedClientLink href={`/account/orders/details/${order.id}`} className="tro-result-link">
        View Full Order Details →
      </LocalizedClientLink>
    </div>
  )
}

const TrackOrderTemplate: React.FC = () => {
  const [orderNumber, setOrderNumber] = useState("")
  const [emailOrPostcode, setEmailOrPostcode] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<HttpTypes.StoreOrder | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [needsAuth, setNeedsAuth] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [validationErrors, setValidationErrors] = useState({ order: false, email: false })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const vErrors = {
      order: !orderNumber.trim(),
      email: !emailOrPostcode.trim(),
    }
    setValidationErrors(vErrors)
    if (vErrors.order || vErrors.email) return

    setLoading(true)
    setResult(null)
    setError(null)
    setNeedsAuth(false)
    setSubmitted(false)

    const res = await findOrderByDisplayId(orderNumber.trim(), emailOrPostcode.trim())
    setLoading(false)
    setSubmitted(true)

    if (res.needsAuth) {
      setNeedsAuth(true)
    } else if (res.found && res.order) {
      setResult(res.order)
    } else {
      setError(res.error ?? "Order not found.")
    }
  }

  return (
    <div className="tro-page">
      {/* Breadcrumb */}
      <nav className="tro-breadcrumb" aria-label="Breadcrumb">
        <LocalizedClientLink href="/" className="tro-breadcrumb-link">Home</LocalizedClientLink>
        <span className="tro-breadcrumb-sep"> › </span>
        <span className="tro-breadcrumb-current">Track my order</span>
      </nav>

      {/* Hero gradient section */}
      <div className="tro-hero">
        <div className="tro-hero-inner">
          <div className="tro-heading-block">
            <h1 className="tro-h1">Track my order</h1>
            <h2 className="tro-subtitle">
              To see the status of your order all you need is your order number and your email address or postcode. This is found on your order confirmation or dispatch email
            </h2>
            <hr className="tro-divider" />

            <form className="tro-form-row" onSubmit={handleSubmit} noValidate>
              {/* Order number */}
              <div className="tro-field-group">
                <div className="tro-field-wrap">
                  <input
                    type="text"
                    className={`tro-input${validationErrors.order ? " tro-input-error" : ""}`}
                    placeholder="Enter order number"
                    value={orderNumber}
                    onChange={(e) => {
                      setOrderNumber(e.target.value)
                      if (e.target.value.trim()) setValidationErrors((v) => ({ ...v, order: false }))
                    }}
                    autoComplete="off"
                  />
                </div>
                {validationErrors.order && (
                  <div className="tro-validation">please enter your order number</div>
                )}
              </div>

              {/* Email or postcode */}
              <div className="tro-field-group">
                <div className="tro-field-wrap">
                  <input
                    type="text"
                    className={`tro-input${validationErrors.email ? " tro-input-error" : ""}`}
                    placeholder="Your Email address or Postcode"
                    value={emailOrPostcode}
                    onChange={(e) => {
                      setEmailOrPostcode(e.target.value)
                      if (e.target.value.trim()) setValidationErrors((v) => ({ ...v, email: false }))
                    }}
                    autoComplete="off"
                  />
                </div>
                {validationErrors.email && (
                  <div className="tro-validation">please enter your email address or postcode</div>
                )}
              </div>

              {/* Submit button */}
              <div className="tro-btn-group">
                <button type="submit" className="tro-btn" disabled={loading}>
                  {loading ? "Searching…" : "Find My Order"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Results area */}
      <div className="tro-results-area">
        {submitted && result && <OrderResult order={result} />}

        {submitted && error && (
          <div className="tro-message tro-message-error">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            {error}
          </div>
        )}

        {submitted && needsAuth && (
          <div className="tro-message tro-message-info">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            <span>
              Please{" "}
              <LocalizedClientLink href="/account" className="tro-msg-link">sign in to your account</LocalizedClientLink>
              {" "}to track your order, or check your confirmation email for tracking details.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

export default TrackOrderTemplate
